import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, OnDestroy, HostListener, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
  ValidatorFn,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core';
import { HttpService } from 'src/app/http/http.service';
import { CanComponentDeactivate } from 'src/app/providers/can-deactivate.guard';
import { GlobalGuardService } from 'src/app/providers/global-guard.service';
import { RobotServiceConfigComponent } from 'src/app/routes/robot-service/config/robot-service-config.component';
import { RobotServicePurchaseComponent } from 'src/app/routes/robot-service/purchase/robot-service-purchase.component';
import { RobotServiceSelectorComponent } from 'src/app/routes/robot-service/select/robot-service-selector.component';
import { RobotOrderHistoryModalComponent } from 'src/app/shared/components/robot-order-history-modal/robot-order-history-modal.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { FormChangeTracker } from 'src/app/shared/utils/form-change-tracker.util';
import { GREETING_CONFIG_MAX_LENGTH, parseGreetingConfig, stringifyGreetingConfig } from 'src/app/shared/utils/greeting-config.util';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';
import { IdUtil } from 'src/app/util/id.util';

@Component({
  selector: 'personal-robot-setting',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzTabsModule,
    NzDividerModule,
    NzGridModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzSwitchModule,
    NzInputNumberModule,
    NzToolTipModule,
    NzUploadModule,
    FormsModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './personal-robot-setting.component.html',
  styleUrls: ['./personal-robot-setting.component.less']
})
export class PersonalRobotSettingComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly modalService = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly fb = inject(FormBuilder);
  private readonly globalGuardService = inject(GlobalGuardService);
  private readonly location = inject(Location, { optional: true });

  loading = false;
  isSaving = false;
  tenantId: string = '';
  serviceNumberId: string = '';
  // 外部嵌入支持：優先使用外部傳入的租戶與服務號 ID
  @Input() tenantIdInput: string = '';
  @Input() serviceNumberIdInput: string = '';
  @Input() serviceNumberTypeInput: string = '';
  @Input() serviceNumberNameInput: string = '';
  // 控制是否展开高级设置区域
  showAdvanced = false;
  robotDetailData: any = null;
  aiServiceStatus: 'Unopened' | 'Active' | 'Inactive' = 'Unopened';
  aiServiceRobotId: string | null = null;
  aiServiceLoading = false;

  robotForm!: FormGroup;
  private initializingData = false;
  private lastRobotServiceId: string | null = null;
  private selectedRobotType: string | null = null;
  // 初始化資料，用於比較判斷是否需要更新 robotId
  private initialRobotServiceId: string | null = null;
  private initialRobotId: string | null = null;
  private formChangeTracker?: FormChangeTracker;
  hasUnsavedChanges = false;
  tenantType: string | null = null;
  private currentServiceNumberType: string | null = null;
  private currentServiceNumberName: string | null = null;
  private confirmedCrossTypeRobotKey: string | null = null;
  purchaseModalTitleKey = 'robot-service.purchase.title.buy';
  purchaseModalSubtitleKey = 'robot-service.purchase.subtitle';
  selectorModalSubtitle = '';
  readonly greetingQuestionMaxLength = GREETING_CONFIG_MAX_LENGTH;
  crossTypeConfirmContent = '';
  crossTypeConfirmNote = '';
  @ViewChild('purchaseModalTitleTpl', { static: true }) purchaseModalTitleTpl!: TemplateRef<{}>;
  @ViewChild('selectorModalTitleTpl', { static: true }) selectorModalTitleTpl!: TemplateRef<{}>;
  @ViewChild('crossTypeAttachConfirmTpl', { static: true }) crossTypeAttachConfirmTpl!: TemplateRef<{}>;

  ngOnInit(): void {
    this.globalGuardService.registerComponent(this);
    this.loadTenantType();
    this.robotForm = this.createRobotForm();

    this.route.paramMap.subscribe(params => {
      // 優先使用外部傳入的值；其次路由；最後兼容舊本地快取
      const routeTenantId = params.get('id') ?? '';
      const qpServiceNumberId = this.route.snapshot.queryParamMap.get('serviceNumberId') ?? '';
      this.tenantId = this.tenantIdInput || routeTenantId || '';
      this.serviceNumberId = this.serviceNumberIdInput || qpServiceNumberId || localStorage.getItem('current_tenant_boss_snid') || '';
      this.currentServiceNumberType = this.serviceNumberTypeInput || this.currentServiceNumberType;
      this.currentServiceNumberName = this.serviceNumberNameInput || this.currentServiceNumberName;
      this.robotForm.get('serviceNumberId')?.setValue(this.serviceNumberId);
      this.loadCurrentServiceNumberMeta();
      if (this.serviceNumberId) {
        this.loadData();
      }
      this.setupListeners();
    });
  }

  ngOnDestroy(): void {
    this.formChangeTracker?.stop();
    this.globalGuardService.unregisterComponent();
  }

  get isEmbeddedInDetail(): boolean {
    return !!this.tenantIdInput || !!this.serviceNumberIdInput;
  }

  get isRobotAttached(): boolean {
    const hasBindingId =
      !!String(this.robotForm?.get('robotServiceId')?.value ?? '').trim() && !!String(this.robotForm?.get('robotId')?.value ?? '').trim();
    const hasRobotIdentity = !!this.robotDetailData?.robotServiceModel || !!String(this.selectedRobotType ?? '').trim();
    return this.robotForm?.get('useRobot')?.value === true && hasBindingId && hasRobotIdentity;
  }

  private loadTenantType(): void {
    const currentTenantRaw = localStorage.getItem('current_tenant');
    if (!currentTenantRaw) {
      this.tenantType = null;
      return;
    }
    try {
      const tenant = JSON.parse(currentTenantRaw);
      this.tenantType = tenant?.type ?? null;
    } catch {
      this.tenantType = null;
    }
  }

  loadData(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_DETAIL}/${this.serviceNumberId}`, {})
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(res => {
        if (res.success && res.data) {
          this.initializingData = true;
          this.robotDetailData = res.data;
          const viewData = this.mapRobotDetailToFormValue(res.data);
          this.robotForm.patchValue(viewData);
          // 從後端返回的 robotServiceModel 中覆蓋顯示名稱，並保存類型供配置判斷
          const model = res.data?.robotServiceModel;
          if (model) {
            if (!String(res.data?.robotName ?? '').trim() && model.name) {
              this.robotForm.patchValue({ robotName: model.name });
            }
            this.selectedRobotType = this.getRobotType(res.data) as string | null;
          } else {
            this.selectedRobotType = this.getRobotType(res.data) as string | null;
          }
          const hasRobot = !!(this.robotForm.get('robotServiceId')?.value || this.robotForm.get('robotId')?.value);
          const useRobot = typeof res.data?.useRobot === 'boolean' ? res.data.useRobot : hasRobot;
          this.robotForm.patchValue({ useRobot }, { emitEvent: false });
          this.aiServiceStatus = res.data?.aiServiceStatus || (useRobot ? 'Active' : hasRobot ? 'Inactive' : 'Unopened');
          this.aiServiceRobotId = res.data?.robotId || null;
          // 记录初始 robotServiceId，避免初始化阶段误生成 robotId
          this.lastRobotServiceId = this.robotForm.get('robotServiceId')?.value ?? null;
          // 同時記錄初始化的 robotServiceId 與 robotId，用於後續比較
          this.initialRobotServiceId = this.lastRobotServiceId;
          this.initialRobotId = this.robotForm.get('robotId')?.value ?? null;
          this.initializingData = false;

          // 重置表單初始快照並啟用變更檢測
          this.formChangeTracker?.stop();
          this.resetChangeDetection();
          this.setupFormChangeDetection();
        }
      });
  }
  /** 以同一個接口切換服務號 AI 服務的開啟或關閉狀態。 */
  setAiServiceStatus(enabled: boolean): void {
    if (!this.serviceNumberId || this.aiServiceLoading) return;
    this.aiServiceLoading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_SERVICENUMBER_AI_SERVICE_STATUS}/${this.serviceNumberId}`, {
        enabled,
        identityType: this.getAiServiceIdentityType()
      })
      .pipe(finalize(() => (this.aiServiceLoading = false)))
      .subscribe({
        next: res => {
          if (res?.success) {
            this.aiServiceStatus = res.data?.status || (enabled ? 'Active' : 'Inactive');
            this.aiServiceRobotId = res.data?.robotId || this.aiServiceRobotId;
            this.message.success(enabled ? 'AI 服務已開啟' : 'AI 服務已關閉');
            this.loadData();
          } else {
            showFallbackHttpError(this.message, res?.msg, 'AI 服務操作失敗');
          }
        },
        error: () => this.message.error('AI 服務操作失敗，請稍後重試')
      });
  }

  /** 依服務號類型決定 AI 服務使用的機器人身份類型，與購買流程保持一致。 */
  private getAiServiceIdentityType(): 'VirtualPersona' | 'VirtualEmployee' {
    return this.getEffectiveServiceNumberType() === 'Boss' ? 'VirtualPersona' : 'VirtualEmployee';
  }

  isAiServiceActive(): boolean {
    return this.aiServiceStatus === 'Active';
  }

  openRobotSelector(): void {
    this.updateSelectorModalSubtitle(0);
    const modal = this.modalService.create({
      nzClassName: 'personal-robot-modal-shell personal-robot-selector-modal',
      nzTitle: this.selectorModalTitleTpl,
      nzContent: RobotServiceSelectorComponent,
      nzWidth: '900px',
      nzFooter: null,
      nzData: {
        selectedRobotId: this.robotForm.get('robotId')?.value,
        tenantId: this.tenantId,
        currentServiceNumberId: this.serviceNumberId,
        serviceNumberType: this.getEffectiveServiceNumberType(),
        onAttachRobot: (robot: any) => this.attachRobotAndSave(robot),
        onDetachRobot: () => this.detachRobotAndSave(),
        onCountChange: (count: number) => this.updateSelectorModalSubtitle(count)
      }
    });

    modal.afterClose.subscribe(result => {
      if (result?.action === 'unbind') {
        this.robotForm.patchValue({
          robotServiceId: null,
          robotId: null,
          robotSubId: null,
          robotName: null,
          useRobot: false
        });
        this.selectedRobotType = null;
        this.confirmedCrossTypeRobotKey = null;
        this.robotForm.markAsDirty();
        this.robotForm.updateValueAndValidity();
        return;
      }

      if (result && (result.robotId || result.id || result.robotServiceId)) {
        this.robotForm.patchValue({
          robotServiceId: result.id ?? result.robotServiceId ?? null,
          robotId: result.robotId ?? null,
          robotSubId: result.robotSubId ?? null,
          robotName: result.name ?? null,
          useRobot: true
        });
        // 保存選擇後的類型
        this.selectedRobotType = this.getRobotType(result) as string | null;
        this.confirmedCrossTypeRobotKey = null;
        this.robotForm.markAsDirty();
        this.robotForm.updateValueAndValidity();
        if (result.action === 'config') {
          this.openRobotConfig({
            robotId: result.robotId ?? null,
            serviceNumberId: result.serviceNumberId ?? null
          });
        }
      }
    });
  }

  private updateSelectorModalSubtitle(count: number): void {
    const tpl = this.i18n.fanyi('robot-service.my.subtitle') || '';
    this.selectorModalSubtitle = tpl.replace('{count}', String(count));
  }

  private async attachRobotAndSave(robot: any): Promise<boolean> {
    this.robotForm.patchValue({
      robotServiceId: robot?.id ?? robot?.robotServiceId ?? null,
      robotId: robot?.robotId ?? null,
      robotSubId: robot?.robotSubId ?? null,
      robotName: robot?.name ?? null,
      serviceNumberId: this.serviceNumberId,
      useRobot: true
    });
    this.syncRobotDetailAfterAttach(robot);
    const robotTimeoutTime = this.robotForm.get('robotTimeoutTime')?.value;
    if (robotTimeoutTime === null || robotTimeoutTime === undefined || robotTimeoutTime === '') {
      this.robotForm.get('robotTimeoutTime')?.setValue(5);
    }
    this.selectedRobotType = this.getRobotType(robot) as string | null;
    this.confirmedCrossTypeRobotKey = null;
    return this.saveServiceRobotAsync();
  }

  private async detachRobotAndSave(): Promise<boolean> {
    this.robotForm.patchValue({
      serviceNumberId: this.serviceNumberId,
      robotServiceId: null,
      robotId: null,
      robotSubId: null,
      robotName: null,
      useRobot: false
    });
    this.robotDetailData = {
      ...(this.robotDetailData ?? {}),
      useRobot: false,
      robotName: null,
      robotServiceId: null,
      robotId: null,
      robotSubId: null,
      expireTime: null,
      status: null,
      robotStatus: null,
      robotServiceModel: null
    };
    this.selectedRobotType = null;
    this.confirmedCrossTypeRobotKey = null;
    return this.saveServiceRobotAsync();
  }

  private openRobotPurchaseModal(ctx?: any): void {
    const mode = ctx?.mode === 'renew' ? 'renew' : 'buy';
    const robotId = ctx?.robotId ?? null;
    this.purchaseModalTitleKey = mode === 'renew' ? 'robot-service.purchase.title.renew' : 'robot-service.purchase.title.buy';
    this.purchaseModalSubtitleKey =
      ctx?.initialStep === 'payment' ? 'robot-service.purchase.subtitle.payment' : 'robot-service.purchase.subtitle';
    const modal = this.modalService.create({
      nzClassName: 'personal-robot-modal-shell personal-robot-purchase-modal',
      nzTitle: this.purchaseModalTitleTpl,
      nzContent: RobotServicePurchaseComponent,
      nzWidth: '760px',
      nzFooter: null,
      nzData: {
        mode,
        initialStep: ctx?.initialStep === 'payment' ? 'payment' : 'packages',
        tenantId: this.tenantId || null,
        robotId,
        name: ctx?.name ?? null,
        serviceNumberType: this.getEffectiveServiceNumberType(),
        selectedPackageCode: ctx?.selectedPackageCode ?? null,
        trialAvailable: typeof ctx?.trialAvailable === 'boolean' ? ctx.trialAvailable : undefined,
        trialUsed: typeof ctx?.trialUsed === 'boolean' ? ctx.trialUsed : undefined,
        onViewStepChange: (step: 'packages' | 'payment') => {
          this.purchaseModalSubtitleKey =
            step === 'payment' ? 'robot-service.purchase.subtitle.payment' : 'robot-service.purchase.subtitle';
        }
      }
    });
    modal.afterClose.subscribe(result => {
      this.purchaseModalSubtitleKey = 'robot-service.purchase.subtitle';
      if (result?.refresh) {
        setTimeout(() => this.openRobotSelector(), 0);
      }
    });
  }

  openRobotConfig(configContext?: { robotId?: string | null; serviceNumberId?: string | null }): void {
    this.navigateToConfig(configContext);
  }

  private navigateToConfig(configContext?: { robotId?: string | null; serviceNumberId?: string | null }): void {
    const robotId = configContext?.robotId ?? this.robotForm.get('robotId')?.value;
    const serviceNumberId = configContext?.serviceNumberId ?? this.serviceNumberId;
    const returnUrl = this.isEmbeddedInDetail ? this.getEmbeddedReturnUrl() : undefined;

    this.router.navigate(['robot-service/config', this.tenantId], {
      queryParams: {
        robotId,
        serviceNumberId,
        returnUrl
      }
    });
  }

  /**
   * Keep the detail page on the robot tab after returning from robot config.
   * The detail component updates the address bar directly, so Router.url can miss
   * the currently selected tab; explicitly restoring it here avoids falling back
   * to the basic information tab.
   */
  private getEmbeddedReturnUrl(): string {
    const rawUrl = this.location?.path(true) || this.router.url || '';
    const [path, hash = ''] = rawUrl.split('#', 2);
    const [pathname, query = ''] = path.split('?', 2);
    const queryParams = new URLSearchParams(query);
    queryParams.set('tab', 'robot');
    const queryString = queryParams.toString();
    return `${pathname}${queryString ? `?${queryString}` : ''}${hash ? `#${hash}` : ''}`;
  }

  private saveServiceRobotAsync(): Promise<boolean> {
    return this.validateAndSaveRobotSetting();
  }

  private setupListeners() {
    // 根据是否选择了机器人服务，在变更时生成 robotId
    this.robotForm.get('robotServiceId')?.valueChanges.subscribe(val => {
      if (this.initializingData) {
        this.lastRobotServiceId = val ?? null;
        return;
      }

      if (val !== this.lastRobotServiceId) {
        this.lastRobotServiceId = val ?? null;
        if (val) {
          // 若選擇的服務與初始化資料一致，則不修改 robotId（恢復初始值）
          if (this.initialRobotServiceId !== null && val === this.initialRobotServiceId) {
            this.robotForm.get('robotId')?.setValue(this.initialRobotId ?? null);
          } else {
            this.robotForm.get('robotId')?.setValue(`AileAI-${IdUtil.fastSimpleUUID()}`);
          }
        } else {
          this.robotForm.get('robotId')?.setValue(null);
        }
      }
    });

    // 由 AI优先(useRobotPriorityService) 控制机器人相关字段的启用
    this.robotForm.get('useRobotPriorityService')?.valueChanges.subscribe(value => {
      this.updateRobotControlsState(value);
    });

    // 根据 useRobot 动态切换 robotServiceId / robotId 的必填校验
    const applyRobotValidators = (enabled: boolean) => {
      const svc = this.robotForm.get('robotServiceId');
      const rid = this.robotForm.get('robotId');
      svc?.setValidators([]);
      rid?.setValidators(enabled ? [Validators.required] : []);
      svc?.updateValueAndValidity({ emitEvent: false });
      rid?.updateValueAndValidity({ emitEvent: false });
    };

    const useRobotInitial = this.robotForm.get('useRobot')?.value;
    applyRobotValidators(!!useRobotInitial);

    this.robotForm.get('useRobot')?.valueChanges.subscribe(enabled => {
      applyRobotValidators(!!enabled);
      if (this.initializingData) {
        return;
      }
      this.robotDetailData = {
        ...(this.robotDetailData ?? {}),
        useRobot: !!enabled
      };
    });

    this.setupAbnormalConditionListeners();
    this.setupSensitiveWordsListeners();
    this.setupGreetingQuestionListeners();
    this.setupHandOverListeners();
    this.setupRobotConfigAvailabilityListeners();
  }

  private setupRobotConfigAvailabilityListeners(): void {
    ['useRobot', 'robotId', 'robotServiceId'].forEach(fieldName => {
      this.robotForm.get(fieldName)?.valueChanges.subscribe(() => this.updateRobotConfigAvailability());
    });
    this.updateRobotConfigAvailability();
  }

  private updateRobotConfigAvailability(): void {
    const attached = this.isRobotAttached;
    const fields = [
      'alias',
      'robotTimeoutTime',
      'useConsultAI',
      'useAssistAI',
      'useWarning',
      'useRobotPriorityService',
      'useSensitiveWordsWarning',
      'useSensitiveWords',
      'sensitiveWordsCount',
      'sensitiveWordsReplyText',
      'useGreetingQuestion',
      'greetingQuestionText',
      'useHandOverText',
      'handOverText',
      'useHandOverTextReply',
      'handOverTextReplyText',
      'useCumulativeMiss',
      'cumulativeMissCount',
      'useContinuousMiss',
      'continuousMissCount',
      'useSameQuestion',
      'sameQuestionCount',
      'useSameResponse',
      'sameResponseCount',
      'useVoiceToText'
    ];

    fields.forEach(fieldName => {
      const control = this.robotForm.get(fieldName);
      if (!control) return;
      if (attached) {
        if (control.disabled) control.enable({ emitEvent: false });
      } else if (control.enabled) {
        control.disable({ emitEvent: false });
      }
    });

    if (attached) {
      this.setupAbnormalConditionListenersState();
      this.setupSensitiveWordsListenersState();
      this.setupGreetingQuestionListenersState();
      this.setupHandOverListenersState();
    }
  }

  private setupAbnormalConditionListeners() {
    const abnormalConditionMap: Record<string, string[]> = {
      useCumulativeMiss: ['cumulativeMissCount'],
      useContinuousMiss: ['continuousMissCount'],
      useSameQuestion: ['sameQuestionCount'],
      useSameResponse: ['sameResponseCount']
    };

    Object.entries(abnormalConditionMap).forEach(([switchName, controlledFields]) => {
      this.robotForm.get(switchName)?.valueChanges.subscribe(enabled => {
        controlledFields.forEach(fieldName => {
          const control = this.robotForm.get(fieldName);
          if (enabled && control?.disabled) control.enable({ emitEvent: false });
          else if (!enabled && control?.enabled) control?.disable({ emitEvent: false });
        });
      });

      const switchEnabled = this.robotForm.get(switchName)?.value;
      controlledFields.forEach(fieldName => {
        const control = this.robotForm.get(fieldName);
        if (switchEnabled && control?.disabled) control.enable({ emitEvent: false });
        else if (!switchEnabled && control?.enabled) control?.disable({ emitEvent: false });
      });
    });
  }

  private setupAbnormalConditionListenersState(): void {
    const map: Record<string, string> = {
      useCumulativeMiss: 'cumulativeMissCount',
      useContinuousMiss: 'continuousMissCount',
      useSameQuestion: 'sameQuestionCount',
      useSameResponse: 'sameResponseCount'
    };
    Object.entries(map).forEach(([switchName, fieldName]) => {
      const control = this.robotForm.get(fieldName);
      const enabled = this.robotForm.get(switchName)?.value === true;
      if (enabled && control?.disabled) control.enable({ emitEvent: false });
      else if (!enabled && control?.enabled) control.disable({ emitEvent: false });
    });
  }

  private setupSensitiveWordsListeners() {
    // useSensitiveWords 控制：是否展示敏感词相关设置（计数与是否提示）
    this.robotForm.get('useSensitiveWords')?.valueChanges.subscribe(enabled => {
      const controlledFields = ['useSensitiveWordsWarning', 'sensitiveWordsCount', 'sensitiveWordsReplyText'];
      controlledFields.forEach(fieldName => {
        const control = this.robotForm.get(fieldName);
        if (enabled && control?.disabled) control.enable({ emitEvent: false });
        else if (!enabled && control?.enabled) control?.disable({ emitEvent: false });
      });
    });

    // 初始化时根据 useSensitiveWords 同步相关控件状态（包含回复文本）
    const sensitiveEnabled = this.robotForm.get('useSensitiveWords')?.value;
    const initControlledFields = ['useSensitiveWordsWarning', 'sensitiveWordsCount', 'sensitiveWordsReplyText'];
    initControlledFields.forEach(fieldName => {
      const control = this.robotForm.get(fieldName);
      if (sensitiveEnabled && control?.disabled) control.enable({ emitEvent: false });
      else if (!sensitiveEnabled && control?.enabled) control?.disable({ emitEvent: false });
    });
  }

  private setupSensitiveWordsListenersState(): void {
    const enabled = this.robotForm.get('useSensitiveWords')?.value === true;
    ['useSensitiveWordsWarning', 'sensitiveWordsCount', 'sensitiveWordsReplyText'].forEach(fieldName => {
      const control = this.robotForm.get(fieldName);
      if (enabled && control?.disabled) control.enable({ emitEvent: false });
      else if (!enabled && control?.enabled) control.disable({ emitEvent: false });
    });
  }

  private setupGreetingQuestionListeners() {
    this.robotForm.get('useGreetingQuestion')?.valueChanges.subscribe(enabled => {
      const control = this.robotForm.get('greetingQuestionText');
      if (enabled && control?.disabled) control?.enable({ emitEvent: false });
      else if (!enabled && control?.enabled) control?.disable({ emitEvent: false });
    });

    const greetingQuestionEnabled = this.robotForm.get('useGreetingQuestion')?.value;
    const control = this.robotForm.get('greetingQuestionText');
    if (greetingQuestionEnabled && control?.disabled) control?.enable({ emitEvent: false });
    else if (!greetingQuestionEnabled && control?.enabled) control?.disable({ emitEvent: false });
  }

  private setupGreetingQuestionListenersState(): void {
    const enabled = this.robotForm.get('useGreetingQuestion')?.value === true;
    const control = this.robotForm.get('greetingQuestionText');
    if (enabled && control?.disabled) control.enable({ emitEvent: false });
    else if (!enabled && control?.enabled) control.disable({ emitEvent: false });
  }

  private setupHandOverListeners() {
    this.robotForm.get('useHandOverText')?.valueChanges.subscribe(enabled => {
      const control = this.robotForm.get('handOverText');
      if (enabled && control?.disabled) control?.enable({ emitEvent: false });
      else if (!enabled && control?.enabled) control?.disable({ emitEvent: false });
    });

    const handOverTextEnabled = this.robotForm.get('useHandOverText')?.value;
    const handOverTextControl = this.robotForm.get('handOverText');
    if (handOverTextEnabled && handOverTextControl?.disabled) handOverTextControl?.enable({ emitEvent: false });
    else if (!handOverTextEnabled && handOverTextControl?.enabled) handOverTextControl?.disable({ emitEvent: false });

    this.robotForm.get('useHandOverTextReply')?.valueChanges.subscribe(enabled => {
      const control = this.robotForm.get('handOverTextReplyText');
      if (enabled && control?.disabled) control?.enable({ emitEvent: false });
      else if (!enabled && control?.enabled) control?.disable({ emitEvent: false });
    });

    const handOverTextReplyEnabled = this.robotForm.get('useHandOverTextReply')?.value;
    const handOverTextReplyControl = this.robotForm.get('handOverTextReplyText');
    if (handOverTextReplyEnabled && handOverTextReplyControl?.disabled) handOverTextReplyControl?.enable({ emitEvent: false });
    else if (!handOverTextReplyEnabled && handOverTextReplyControl?.enabled) handOverTextReplyControl?.disable({ emitEvent: false });
  }

  private setupHandOverListenersState(): void {
    [
      ['useHandOverText', 'handOverText'],
      ['useHandOverTextReply', 'handOverTextReplyText']
    ].forEach(([switchName, fieldName]) => {
      const enabled = this.robotForm.get(switchName)?.value === true;
      const control = this.robotForm.get(fieldName);
      if (enabled && control?.disabled) control.enable({ emitEvent: false });
      else if (!enabled && control?.enabled) control.disable({ emitEvent: false });
    });
  }

  private updateRobotControlsState(robotEnabled: boolean) {
    const robotControlledFields = ['robotServiceId', 'robotId', 'robotSubId'];

    robotControlledFields.forEach(field => {
      const control = this.robotForm.get(field);
      if (robotEnabled && control?.disabled) control.enable({ emitEvent: false });
      else if (!robotEnabled && control?.enabled) control?.disable({ emitEvent: false });
    });
  }

  private createRobotForm(): FormGroup {
    const group = this.fb.group(
      {
        serviceNumberId: [this.serviceNumberId],
        useRobot: [false],
        robotServiceId: [{ value: null, disabled: true }],
        robotName: [null],
        robotId: [{ value: null, disabled: true }],
        robotSubId: [{ value: null, disabled: true }],
        alias: [{ value: null }],
        robotTimeoutTime: [{ value: 5 }, [Validators.required]],
        useConsultAI: [false],
        useAssistAI: [false],
        useWarning: [false],
        useSensitiveWordsWarning: [false],
        useRobotPriorityService: [false],
        useSensitiveWords: [false],
        sensitiveWordsCount: [{ value: 3, disabled: true }, [Validators.required]],
        sensitiveWordsReplyText: [{ value: null, disabled: false }],
        useGreetingQuestion: [false],
        greetingQuestionText: [
          { value: null, disabled: true },
          [Validators.required, Validators.maxLength(GREETING_CONFIG_MAX_LENGTH), this.trimmedRequiredValidator()]
        ],
        useHandOverText: [false],
        handOverText: [{ value: null, disabled: true }, [Validators.required]],
        useHandOverTextReply: [false],
        handOverTextReplyText: [{ value: null, disabled: true }, [Validators.required]],
        useCumulativeMiss: [false],
        cumulativeMissCount: [{ value: 3, disabled: true }, [Validators.required]],
        useContinuousMiss: [false],
        continuousMissCount: [{ value: 3, disabled: true }, [Validators.required]],
        useSameQuestion: [false],
        sameQuestionCount: [{ value: 3, disabled: true }, [Validators.required]],
        useSameResponse: [false],
        sameResponseCount: [{ value: 3, disabled: true }, [Validators.required]],
        useVoiceToText: [false]
      },
      { validators: [this.requireRobotFieldsWhenUseRobot()] }
    );

    return group;
  }

  private requireRobotFieldsWhenUseRobot(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const useRobot = control.get('useRobot')?.value;
      if (useRobot) {
        const robotId = control.get('robotId')?.value;
        if (!robotId) {
          return { robotRequired: true };
        }
      }
      return null;
    };
  }

  saveServiceRobot() {
    void this.validateAndSaveRobotSetting();
  }

  private async validateAndSaveRobotSetting(): Promise<boolean> {
    if (this.robotForm.invalid) {
      Object.values(this.robotForm.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      const timeoutCtrl = this.robotForm.get('robotTimeoutTime');
      if (timeoutCtrl?.invalid) {
        this.message.warning(this.i18n.fanyi('servicenumber.robot.timeout-time-required'));
        this.scrollToElementById('robotTimeoutTime');
      }
      return false;
    }

    const formData = this.robotForm.getRawValue();
    const confirmed = await this.confirmCrossTypeAttachIfNeeded(formData);
    if (!confirmed) {
      return false;
    }

    const savePayload = this.buildRobotSavePayload(formData);

    this.isSaving = true;
    return new Promise(resolve => {
      this.http
        .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_SAVE, savePayload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: res => {
            if (res.success) {
              this.message.success(this.i18n.fanyi('servicenumber.robot.save-success'));
              this.robotForm.markAsPristine();
              this.resetChangeDetection();
              resolve(true);
            } else {
              showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('servicenumber.robot.save-failed'));
              resolve(false);
            }
          },
          error: () => {
            this.message.error(this.i18n.fanyi('servicenumber.robot.save-failed-retry'));
            resolve(false);
          }
        });
    });
  }

  private buildRobotSavePayload(formData: any): any {
    const payload: any = { ...formData };
    payload.greetingQuestionText = this.isRobotAttached ? this.normalizeGreetingQuestionForSave(payload?.greetingQuestionText) : null;
    payload.useRobot = payload?.useRobot === true;
    payload.serviceNumberId = String(this.serviceNumberId || payload?.serviceNumberId || '').trim() || payload?.serviceNumberId || null;
    const timeoutValue = payload?.robotTimeoutTime;
    const timeout = Number(timeoutValue);
    payload.robotTimeoutTime =
      timeoutValue === null || timeoutValue === undefined || timeoutValue === '' || !Number.isFinite(timeout) ? 5 : timeout;
    return payload;
  }

  private mapRobotDetailToFormValue(data: any): any {
    // Always present a readable message in the form. The save format is
    // decided separately from the currently attached robot type.
    const greetingConfig = parseGreetingConfig(data?.greetingQuestionText);
    return {
      ...data,
      greetingQuestionText: greetingConfig ? greetingConfig.message || null : (data?.greetingQuestionText ?? null)
    };
  }

  getGreetingQuestionErrorKey(): string {
    const control = this.robotForm.get('greetingQuestionText');
    if (control?.hasError('maxlength')) {
      return 'servicenumber.robot.greeting-text-too-long';
    }
    return 'servicenumber.robot.greeting-text-required';
  }

  hasSelectedRobot(): boolean {
    return !!(this.robotForm.get('robotServiceId')?.value || this.robotForm.get('robotId')?.value);
  }

  getRobotDisplayNameForCard(): string {
    return (
      String(this.robotForm.get('robotName')?.value ?? '').trim() ||
      String(this.robotDetailData?.robotName ?? '').trim() ||
      String(this.robotDetailData?.robotServiceModel?.name ?? '').trim() ||
      this.i18n.fanyi('servicenumber.robot.ai-service-name')
    );
  }

  getRobotIdentityLabel(): string {
    const typeCandidates = [
      this.robotDetailData?.robotServiceModel?.identityType,
      this.robotDetailData?.identityType,
      this.selectedRobotType,
      this.robotDetailData?.robotServiceModel?.type,
      this.robotDetailData?.type
    ];
    const preferred = typeCandidates.map(candidate => String(candidate ?? '').trim()).find(Boolean);
    if (preferred === 'VirtualPersona') {
      return this.i18n.fanyi('robot-service.identity.virtual-persona');
    }
    if (preferred === 'VirtualEmployee') {
      return this.i18n.fanyi('robot-service.identity.virtual-employee');
    }
    return '';
  }

  getRobotIdentityClass(): string {
    const label = this.getRobotIdentityLabel();
    if (label === this.i18n.fanyi('robot-service.identity.virtual-persona')) {
      return 'is-virtual-persona';
    }
    if (label === this.i18n.fanyi('robot-service.identity.virtual-employee')) {
      return 'is-virtual-employee';
    }
    return '';
  }

  getRobotIdentifierForCard(): string {
    return (
      String(this.robotForm.get('robotId')?.value ?? '').trim() ||
      String(this.robotForm.get('robotServiceId')?.value ?? '').trim() ||
      String(this.robotDetailData?.robotId ?? '').trim() ||
      String(this.robotDetailData?.robotServiceId ?? '').trim() ||
      this.i18n.fanyi('servicenumber.robot.ai-service-not-opened')
    );
  }

  getRobotExpireDateText(): string {
    const expireTime = this.getRobotExpireTimestamp();
    if (expireTime === null) {
      return '-';
    }
    const date = new Date(expireTime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getRobotExpireToneClass(): string {
    const expireTime = this.getRobotExpireTimestamp();
    if (expireTime === null) {
      return 'is-empty';
    }
    if (this.isRobotExpired()) {
      return 'is-expired';
    }
    if (this.isRobotExpiringSoon()) {
      return 'is-warning';
    }
    return 'is-normal';
  }

  getRobotStatusLabel(): string {
    const explicit = this.getExplicitRobotStatus();
    if (explicit) {
      return explicit;
    }
    if (!this.hasSelectedRobot()) {
      return this.i18n.fanyi('servicenumber.robot.ai-service-not-opened');
    }
    if (this.isRobotExpired()) {
      return this.i18n.fanyi('robot-service.my.expired');
    }
    if (this.robotForm.get('useRobot')?.value === false) {
      return this.i18n.fanyi('robot-service.my.binding.disabled');
    }
    return this.i18n.fanyi('robot-service.my.available');
  }

  getRobotStatusClass(): string {
    if (!this.hasSelectedRobot()) {
      return 'is-idle';
    }
    if (this.isRobotExpired()) {
      return 'is-expired';
    }
    if (this.robotForm.get('useRobot')?.value === false) {
      return 'is-disabled';
    }
    return 'is-active';
  }

  detachRobot(): void {
    void this.detachRobotAndSave();
  }

  openCurrentRobotPackageModal(): void {
    const robotId = String(this.robotForm.get('robotId')?.value ?? '').trim();
    if (!robotId) {
      return;
    }
    this.modalService.create({
      nzTitle: this.i18n.fanyi('robot-service.order.history.title'),
      nzContent: RobotOrderHistoryModalComponent,
      nzWidth: '880px',
      nzFooter: null,
      nzClassName: 'personal-robot-modal-shell robot-order-history-modal',
      nzData: {
        robotId,
        name: this.getRobotDisplayNameForCard(),
        pageIndex: 0,
        pageSize: 20,
        orderBy: 'createTime',
        direction: 'desc'
      }
    });
  }

  private syncRobotDetailAfterAttach(robot: any): void {
    const nextModel = {
      ...(this.robotDetailData?.robotServiceModel ?? {}),
      id: robot?.id ?? robot?.robotServiceId ?? this.robotDetailData?.robotServiceModel?.id ?? null,
      name: robot?.name ?? this.robotDetailData?.robotServiceModel?.name ?? null,
      type: robot?.type ?? this.robotDetailData?.robotServiceModel?.type ?? null,
      identityType: robot?.identityType ?? this.robotDetailData?.robotServiceModel?.identityType ?? null,
      expireTime: robot?.expireTime ?? this.robotDetailData?.robotServiceModel?.expireTime ?? this.robotDetailData?.expireTime ?? null,
      status: robot?.status ?? this.robotDetailData?.robotServiceModel?.status ?? null
    };
    this.robotDetailData = {
      ...(this.robotDetailData ?? {}),
      useRobot: true,
      robotName: robot?.name ?? this.robotDetailData?.robotName ?? null,
      robotServiceId: robot?.id ?? robot?.robotServiceId ?? null,
      robotId: robot?.robotId ?? null,
      robotSubId: robot?.robotSubId ?? null,
      expireTime: robot?.expireTime ?? this.robotDetailData?.expireTime ?? nextModel.expireTime ?? null,
      status: robot?.status ?? this.robotDetailData?.status ?? null,
      robotStatus: robot?.robotStatus ?? this.robotDetailData?.robotStatus ?? null,
      robotServiceModel: nextModel
    };
  }

  private getRobotExpireTimestamp(): number | null {
    const candidates = [
      this.robotDetailData?.expireTime,
      this.robotDetailData?.robotExpireTime,
      this.robotDetailData?.robotServiceModel?.expireTime
    ];
    for (const candidate of candidates) {
      const value = Number(candidate ?? 0);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }
    return null;
  }

  private isRobotExpiringSoon(days: number = 7): boolean {
    const expireTime = this.getRobotExpireTimestamp();
    if (expireTime === null || expireTime <= Date.now()) {
      return false;
    }
    return expireTime - Date.now() <= days * 24 * 60 * 60 * 1000;
  }

  private isRobotExpired(): boolean {
    const expireTime = this.getRobotExpireTimestamp();
    return expireTime !== null && expireTime <= Date.now();
  }

  private getExplicitRobotStatus(): string | null {
    const textCandidates = [
      this.robotDetailData?.robotStatusText,
      this.robotDetailData?.statusText,
      this.robotDetailData?.robotServiceModel?.statusText
    ];
    for (const candidate of textCandidates) {
      const text = String(candidate ?? '').trim();
      if (text) {
        return text;
      }
    }

    const codeCandidates = [
      this.robotDetailData?.robotStatus,
      this.robotDetailData?.status,
      this.robotDetailData?.robotServiceModel?.status
    ];
    for (const candidate of codeCandidates) {
      const normalized = String(candidate ?? '')
        .trim()
        .toLowerCase();
      if (!normalized) {
        continue;
      }
      if (['enable', 'enabled', 'active', 'available', 'normal'].includes(normalized)) {
        return this.i18n.fanyi('robot-service.my.available');
      }
      if (['disable', 'disabled', 'inactive', 'off'].includes(normalized)) {
        return this.i18n.fanyi('robot-service.my.binding.disabled');
      }
      if (['expired', 'expire'].includes(normalized)) {
        return this.i18n.fanyi('robot-service.my.expired');
      }
    }
    return null;
  }

  private normalizeGreetingQuestionForSave(value: unknown): string | null {
    const message = String(value ?? '').trim();
    if (!message) return null;
    return this.isGptRobotType(this.selectedRobotType) ? stringifyGreetingConfig(message) : message;
  }

  private getRobotType(data?: any): unknown {
    return data?.robotServiceModel?.type ?? data?.type ?? data?.robotServiceModel?.identityType ?? data?.identityType ?? null;
  }

  private isGptRobotType(type: unknown): boolean {
    return (
      String(type ?? '')
        .trim()
        .toLowerCase() === 'gpt'
    );
  }

  private trimmedRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '').trim();
      return value ? null : { required: true };
    };
  }

  private loadCurrentServiceNumberMeta(): void {
    if (!this.serviceNumberId) {
      return;
    }
    this.currentServiceNumberType = this.serviceNumberTypeInput || this.currentServiceNumberType;
    this.currentServiceNumberName = this.serviceNumberNameInput || this.currentServiceNumberName;
    if (this.currentServiceNumberType && this.currentServiceNumberName) {
      return;
    }
    this.http.post(`${ConstantUtil.ADMIN_SERVICENUMBER_DETAIL}/${this.serviceNumberId}`, {}).subscribe({
      next: res => {
        if (!res?.success) return;
        const data = res?.data?.serviceNumberModel ?? res?.data ?? {};
        this.currentServiceNumberType = data?.type ?? this.currentServiceNumberType;
        this.currentServiceNumberName = data?.name ?? this.currentServiceNumberName;
      },
      error: () => {}
    });
  }

  private getEffectiveServiceNumberType(): string | null {
    return this.serviceNumberTypeInput || this.currentServiceNumberType || null;
  }

  private async confirmCrossTypeAttachIfNeeded(formData: any): Promise<boolean> {
    if (!formData?.robotId || !formData?.robotServiceId || !this.selectedRobotType) {
      return true;
    }

    const serviceNumberType = this.getEffectiveServiceNumberType();
    const needsBusinessEmployeeConfirm = serviceNumberType === 'Boss' && this.selectedRobotType === 'VirtualEmployee';
    const needsServicePersonaConfirm = serviceNumberType !== 'Boss' && this.selectedRobotType === 'VirtualPersona';
    if (!needsBusinessEmployeeConfirm && !needsServicePersonaConfirm) {
      this.confirmedCrossTypeRobotKey = null;
      return true;
    }

    const confirmationKey = `${formData.robotId}|${serviceNumberType}|${this.selectedRobotType}`;
    if (this.confirmedCrossTypeRobotKey === confirmationKey) {
      return true;
    }

    const contentKey = needsBusinessEmployeeConfirm
      ? 'servicenumber.robot.attach.cross-type.business-employee'
      : 'servicenumber.robot.attach.cross-type.service-persona';
    const content = this.i18n
      .fanyi(contentKey)
      .replace(/\{serviceNumberTypeLabel\}/g, this.getCurrentServiceNumberTypeLabel())
      .replace(/\{serviceNumberName\}/g, this.getCurrentServiceNumberDisplayName())
      .replace(/\{robotName\}/g, this.getRobotDisplayName(formData))
      .replace(
        '{defaultRobotType}',
        needsBusinessEmployeeConfirm
          ? this.i18n.fanyi('robot-service.identity.virtual-persona')
          : this.i18n.fanyi('robot-service.identity.virtual-employee')
      )
      .replace(/\{selectedRobotType\}/g, this.getSelectedRobotTypeLabel());
    const note = this.i18n.fanyi('servicenumber.robot.attach.cross-type.note');

    this.crossTypeConfirmContent = content;
    this.crossTypeConfirmNote = note;

    const confirmed = await new Promise<boolean>(resolve => {
      const modalRef: NzModalRef = this.modalService.create({
        nzClassName: 'personal-robot-modal-shell personal-robot-confirm-modal cross-type-attach-confirm-modal',
        nzClosable: false,
        nzMaskClosable: false,
        nzWidth: 520,
        nzFooter: [
          {
            label: this.i18n.fanyi('servicenumber.robot.attach.cross-type.reselect'),
            type: 'default',
            onClick: (): void => {
              resolve(false);
              modalRef.close();
            }
          },
          {
            label: this.i18n.fanyi('servicenumber.robot.attach.cross-type.confirm'),
            type: 'primary',
            onClick: (): void => {
              resolve(true);
              modalRef.close();
            }
          }
        ],
        nzContent: this.crossTypeAttachConfirmTpl
      });
    });

    if (confirmed) {
      this.confirmedCrossTypeRobotKey = confirmationKey;
    }
    return confirmed;
  }

  private getCurrentServiceNumberDisplayName(): string {
    const name = String(this.currentServiceNumberName ?? '').trim();
    return name || this.serviceNumberId || '-';
  }

  private getCurrentServiceNumberTypeLabel(): string {
    const type = String(this.getEffectiveServiceNumberType() ?? '').trim();
    const typeLabelMap: Record<string, string> = {
      Official: this.i18n.fanyi('servicenumber.type.official'),
      Manage: this.i18n.fanyi('servicenumber.type.manage'),
      Professional: this.i18n.fanyi('servicenumber.type.professional'),
      Normal: this.i18n.fanyi('servicenumber.type.normal'),
      Common: this.i18n.fanyi('servicenumber.type.normal'),
      Boss: this.i18n.fanyi('servicenumber.type.boss')
    };
    return typeLabelMap[type] || type || this.i18n.fanyi('servicenumber.type.normal');
  }

  private getRobotDisplayName(formData: any): string {
    const name = String(formData?.robotName ?? '').trim();
    return name || String(formData?.robotId ?? '').trim() || '-';
  }

  private getSelectedRobotTypeLabel(): string {
    const type = String(this.selectedRobotType ?? '').trim();
    if (!type) return this.i18n.fanyi('servicenumber.robot.default-robot-name');
    const map: Record<string, string> = {
      VirtualPersona: this.i18n.fanyi('robot-service.identity.virtual-persona'),
      VirtualEmployee: this.i18n.fanyi('robot-service.identity.virtual-employee'),
      Gpt: this.i18n.fanyi('robot-service.type.gpt'),
      Cbm: this.i18n.fanyi('robot-service.type.cbm')
    };
    return map[type] || type;
  }

  onUseRobotChange(enabled: boolean): void {
    if (enabled) {
      this.robotForm.get('robotTimeoutTime')?.setValue(5);
    }
  }

  // ===== 變更檢測：當表單值回到初始快照時視為未變更 =====
  private setupFormChangeDetection(): void {
    this.formChangeTracker?.stop();
    this.formChangeTracker = new FormChangeTracker(this.robotForm, () => this.robotForm.getRawValue());
    this.formChangeTracker.start(hasChanged => {
      this.hasUnsavedChanges = hasChanged;
    });
  }

  private resetChangeDetection(): void {
    this.formChangeTracker?.reset();
    this.hasUnsavedChanges = false;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges) return;
    $event.preventDefault();
    $event.returnValue = this.i18n.fanyi('servicenumber.robot.unsaved.content');
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.hasUnsavedChanges) return true;
    return new Promise(resolve => {
      this.modalService.confirm({
        nzClassName: 'personal-robot-confirm-modal',
        nzTitle: this.i18n.fanyi('servicenumber.robot.unsaved.title'),
        nzContent: this.i18n.fanyi('servicenumber.robot.unsaved.content'),
        nzOkText: this.i18n.fanyi('servicenumber.robot.unsaved.save-and-go'),
        nzCancelText: this.i18n.fanyi('servicenumber.robot.unsaved.go-without-saving'),
        nzClosable: false,
        nzMaskClosable: false,
        nzOnOk: async () => {
          const ok = await this.saveServiceRobotAsync();
          resolve(ok);
        },
        nzOnCancel: () => {
          this.robotForm.markAsPristine();
          this.resetChangeDetection();
          resolve(true);
        }
      });
    });
  }

  private scrollToElementById(id: string): void {
    try {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = el.querySelector('input') as HTMLInputElement | null;
      (input ?? (el as any))?.focus?.();
    } catch {}
  }
}
